import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Film, Mail, Lock, Chrome, Loader2, Eye, EyeOff } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { Button } from '../../components/atoms/Button';
import { Input } from '../../components/atoms/Input';
import { Card } from '../../components/atoms/Card';

type AuthMode = 'signin' | 'signup';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState<AuthMode>('signin');
  const { signInWithGoogle, signInWithEmail, signUpWithEmail, isLoading, error, clearError } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    clearError();

    if (mode === 'signin') {
      await signInWithEmail(email, password);
    } else {
      await signUpWithEmail(email, password);
    }
  };

  const toggleMode = () => {
    setMode(mode === 'signin' ? 'signup' : 'signin');
    clearError();
  };

  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="p-3 bg-blue-600 rounded-xl shadow-lg shadow-blue-600/20">
            <Film size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-black tracking-tighter uppercase text-white">
            PRODUKCIJSKI <span className="text-blue-500">MASTER</span>
          </h1>
        </div>

        <Card variant="glass" padding="lg" radius="3xl">
          <div className="text-center mb-6">
            <h2 className="text-lg font-bold text-white mb-1">
              {mode === 'signin' ? 'Welcome Back' : 'Create Account'}
            </h2>
            <p className="text-sm text-slate-400">
              {mode === 'signin'
                ? 'Sign in to access your video projects'
                : 'Sign up to start creating amazing videos'}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Google Sign In - Disabled for now */}
          <Button
            variant="ghost"
            className="w-full mb-4 opacity-50 cursor-not-allowed"
            disabled={true}
            leftIcon={<Chrome size={18} />}
          >
            Continue with Google (Coming Soon)
          </Button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-[#0a0a0a] text-slate-500 uppercase tracking-wider">
                Or continue with email
              </span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              leftIcon={<Mail size={16} />}
              required
            />
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                leftIcon={<Lock size={16} />}
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            
            {/* Forgot Password Link - only show in signin mode */}
            {mode === 'signin' && (
              <div className="text-right -mt-2">
                <Link 
                  to="/forgot-password" 
                  className="text-xs text-slate-500 hover:text-blue-500 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
            )}
            
            <Button
              type="submit"
              variant="primary"
              className="w-full"
              isLoading={isLoading}
              disabled={!email || !password || password.length < 6}
            >
              {mode === 'signin' ? 'Sign In' : 'Create Account'}
            </Button>
          </form>

          {/* Toggle Mode */}
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">
              {mode === 'signin' ? "Don't have an account?" : 'Already have an account?'}{' '}
              <button
                type="button"
                onClick={toggleMode}
                className="text-blue-500 hover:text-blue-400 font-medium transition-colors"
              >
                {mode === 'signin' ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>
        </Card>

        <p className="text-center mt-6 text-xs text-slate-600">
          © 2024 ZADAR ARCHIPELAGO PRODUCTIONS
        </p>
      </div>
    </div>
  );
}
